/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';
import {
  createWidgetConfig,
  createDynamicFindemIframe,
  cleanupFindemWidget,
  findemWidgetEventNames,
} from 'helpers/findemWidget';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { useLazyGetNetworkQuery } from 'services/organizationsV2';
import { useLazyGetJobQuery } from 'services/jobsV2';

const getStorageKey = ({ collectionId = '', jobId = '', agentId = '' }) =>
  `findem_public_agent_session_${agentId}_${collectionId}_${jobId}`;

export const useInitializedWidget = () => {
  // Get collectionId from query params
  const urlParams = new URLSearchParams(window.location.search);
  const collectionId = urlParams.get('collection_id');
  const [getNetwork, { isLoading: isLoadingNetwork, isFetching: isFetchingNetwork, data: network }] =
    useLazyGetNetworkQuery();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveSessionToStorage = useCallback(
    (newSessionId, jobId, agentId) => {
      if (collectionId && newSessionId) {
        const storageKey = getStorageKey({ collectionId, jobId, agentId });
        try {
          localStorage.setItem(storageKey, newSessionId);
        } catch (storageError) {
          // Silently fail - session storage is not critical for functionality
        }
      }
    },
    [collectionId],
  );

  const [getJob] = useLazyGetJobQuery();

  // Listen to session change events
  useEffect(() => {
    const handleSessionChange = (event) => {
      const { payload } = event.detail;
      // Check if this session change is for our collection
      const currentUrlParams = new URLSearchParams(window.location.search);
      const agentId = currentUrlParams.get('agent_id');
      if (payload?.sessionId && String(payload?.collection_id) === String(collectionId) && agentId) {
        const jobId = payload?.job_id || currentUrlParams.get('job_id');
        saveSessionToStorage(payload.sessionId, jobId, agentId || '');
      }
    };

    window.addEventListener(findemWidgetEventNames.FINDEM_WIDGET_SESSION_CHANGE, handleSessionChange);

    return () => {
      window.removeEventListener(findemWidgetEventNames.FINDEM_WIDGET_SESSION_CHANGE, handleSessionChange);
    };
  }, [collectionId, saveSessionToStorage]);

  const initializeWidget = useCallback(
    async ({
      token,
      vcInfo,
      vcUrl,
      jobBoardUrl,
      agentId,
      jobId,
      jobDescription,
      jobUrl,
      jobTitle,
      emptyStateTitle,
      emptyStateSubtitle,
      initialPrompts,
      additionalParams,
    }) => {
      setError(null);

      try {
        if (!agentId) {
          throw new Error('Agent ID is required');
        }

        // Create widget configuration adapted for public use
        const config = createWidgetConfig({
          job: null, // No job for public agent view
          user: null, // No authenticated user for public page
          token: token || '', // Token is optional for public agent
          collectionId,
          view: 'shared-agent',
          vcInfo,
          vcUrl,
          jobBoardUrl,
          agentId,
        });

        // Override session metadata for public context
        config.sessionMetadata = {
          source: 'public_agent',
          user_id: 'public_user',
          job_id: jobId || null,
          collection_id: collectionId || null,
          sourcing_campaign_id: null,
        };

        // Override agent params for public agent (no job-specific data)
        config.agentParams = {
          collection_id: collectionId || '',
          job_id: jobId || '',
          job_title: jobTitle || '',
          job_description: jobDescription || '',
          job_url: jobUrl || '',
          vc_info: vcInfo || '',
          vc_url: vcUrl || '',
          is_getro: true,
          job_board_base: jobBoardUrl ? addProtocolToUrl(jobBoardUrl) : '',
          base_url_input: config.agentParams?.base_url_input || '',
          ...additionalParams,
        };

        // Override UI config with values from query parameters if provided
        if (emptyStateTitle !== null && emptyStateTitle !== undefined) {
          config.uiConfig.emptyStateTitle = emptyStateTitle;
        }
        if (emptyStateSubtitle !== null && emptyStateSubtitle !== undefined) {
          config.uiConfig.emptyStateSubtitle = emptyStateSubtitle;
        }
        if (initialPrompts !== null && initialPrompts !== undefined) {
          // Parse initialPrompts if it's a string (comma-separated or JSON)
          if (typeof initialPrompts === 'string') {
            try {
              // Try parsing as JSON array first
              const parsed = JSON.parse(initialPrompts);
              config.uiConfig.initialPrompts = Array.isArray(parsed) ? parsed : [initialPrompts];
            } catch {
              // If not JSON, treat as comma-separated string
              config.uiConfig.initialPrompts = initialPrompts
                .split(',')
                .map((p) => p.trim())
                .filter((p) => p);
            }
          } else if (Array.isArray(initialPrompts)) {
            config.uiConfig.initialPrompts = initialPrompts;
          }
        }

        // Use sessionId from localStorage if available (for the specific job_id)
        const storageKey = getStorageKey({ collectionId, jobId, agentId });
        try {
          const storedSessionId = localStorage.getItem(storageKey);
          if (storedSessionId) {
            config.sessionId = storedSessionId;
          }
        } catch (storageError) {
          // Silently fail - session storage is not critical for functionality
        }

        await createDynamicFindemIframe(config);
        setIsLoading(false);
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    },
    [collectionId],
  );

  // Fetch network and initialize widget
  useEffect(() => {
    (async () => {
      if (!collectionId) {
        setError(new Error('Missing collection ID'));
        setIsLoading(false);
        return;
      }
      const currentUrlParams = new URLSearchParams(window.location.search);
      const token = currentUrlParams.get('jwt');
      const agentId = currentUrlParams.get('agent_id');
      const jobId = currentUrlParams.get('job_id');
      const emptyStateTitle = currentUrlParams.get('empty_state_title');
      const emptyStateSubtitle = currentUrlParams.get('empty_state_subtitle');
      const initialPrompts = currentUrlParams.get('initial_prompts');

      // Extract additional params (all params except the known ones)
      const knownParams = ['icp_id', 'vc_url', 'vc_info'];
      const additionalParams = {};
      currentUrlParams.forEach((value, key) => {
        if (knownParams.includes(key)) {
          additionalParams[key] = value;
        }
      });

      if (!agentId) {
        // Don't throw error, just don't initialize widget - view should still render
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const agentNetwork = await getNetwork(collectionId).unwrap();
        if (!agentNetwork) {
          throw new Error('Network not found');
        }
        // Fetch job data if jobId is provided
        let jobDescription = '';
        let jobUrl = '';
        let jobTitle = '';
        if (jobId) {
          try {
            const jobData = await getJob({ jobId, networkId: collectionId }).unwrap();
            jobTitle = jobData?.title || '';
            jobDescription = jobData?.description || '';
            jobUrl = jobData?.url || '';
          } catch (jobError) {
            // Show error if job not found (404) or other errors
            const errorMessage = jobError?.status === 404 ? 'Job not found' : 'Failed to load job data';
            setError(new Error(errorMessage));
            setIsLoading(false);
            return;
          }
        }
        // Initialize widget
        await initializeWidget({
          token,
          vcInfo: agentNetwork?.description,
          vcUrl: agentNetwork?.domain,
          jobBoardUrl: agentNetwork?.url,
          agentId,
          jobId,
          jobTitle,
          jobDescription,
          jobUrl,
          emptyStateTitle,
          emptyStateSubtitle,
          initialPrompts,
          additionalParams,
        });
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    })();

    // Cleanup on unmount
    return () => {
      cleanupFindemWidget();
    };
  }, [collectionId]);

  return {
    isLoading: isLoadingNetwork || isFetchingNetwork || isLoading,
    error,
    network,
  };
};
